/**
 * VRS ↔ VRI for accounts that hold both services (mobile parity plan P2 74).
 *
 * The web client portal reads the account's `serviceModes` (sign-in user and
 * /api/client/profile) and shows "VRS Calls | VRI Sessions" in the header of
 * both consoles when the account holds both (client-profile.js
 * getServiceModes / renderServiceSwitch). MalkaVRS and MalkaVRI share one
 * account space, and every native build registers the VRI screens, so the
 * MalkaVRS app can offer the same switch. The choice is per visit, as on web.
 */
import { isVrsApp } from '../../base/whitelabel/functions';
/** The entitlement the Relay screens and the Relay portal admit. */
export const RELAY_PILOT_ENTITLEMENT = 'relay.pilot';
function modesOf(value) {
    return Array.isArray(value) ? value.filter((mode) => typeof mode === 'string') : [];
}
/**
 * What a client account may open in the MalkaVRS app (Robert, 2026-10-20:
 * Relay "accessed directly thru the malkavrs app so long as the account has
 * permission"): VRS and VRI from its service modes, Relay with `relay.pilot`.
 * The Relay screens are registered in every build and use the same sign-in.
 */
export function availableServiceViews(serviceModes, entitlements) {
    const modes = modesOf(serviceModes);
    const views = [];
    if (modes.includes('vrs')) {
        views.push('vrs');
    }
    if (modes.includes('vri')) {
        views.push('vri');
    }
    if (modesOf(entitlements).includes(RELAY_PILOT_ENTITLEMENT)) {
        views.push('relay');
    }
    return views;
}
/** True when the account's service modes include both VRS and VRI. */
export function isDualServiceAccount(serviceModes) {
    const modes = modesOf(serviceModes);
    return modes.includes('vrs') && modes.includes('vri');
}
/**
 * The switch is offered in the MalkaVRS app only, to an account with two or
 * more of VRS, VRI and Relay. MalkaVRI and MapleVRI are VRI products; their
 * VRS screens are not part of the app, and Malka Relay is its own app.
 */
export function offersServiceSwitch(serviceModes, vrsApp = isVrsApp(), entitlements) {
    return vrsApp && availableServiceViews(serviceModes, entitlements).length >= 2;
}
/** A VRI session (queue type or room prefix), so the app returns to the VRI console after it. */
export function isVriCall(call) {
    return call?.callType === 'vri' || /^vri-/.test(call?.roomName || '');
}
